import React from 'react';
import { View } from 'react-native';

import Video from 'app/src/elements/Video';
import { Subtitle, Text } from 'app/src/styles';
import { Link } from 'app/src/utils/routing';

import { Step } from '../elements';

const PrintOverlay = () => {
  return (
    <View style={{ gap: 10 }}>
      <Subtitle>Printing Purchase Information Overlay</Subtitle>

      <Text>
        OklahomaTempTag.com provides the option to print purchase information
        (tag expiration date, purchase name, and vehicle year, make and
        model) onto a temporary tag when pre-registering the sale.
      </Text>

      <Text style={{ fontWeight: 600, borderBottomWidth: 1 }}>
        Please note that this feature is experimental, and many printers may
        not be able to print on the temporary tag material or print correctly
        on the size. We are unable to provide printer setup support at this
        time.
      </Text>

      <Video
        url="https://www.youtube.com/embed/rCQ6TNyccX0?si=NpIWvxEG3GY9WWYT"
        title="Print Purchase Information Overlay"
      />

      <View>
        <Text style={{ fontWeight: 600 }}>Printer considerations:</Text>

        <Step>
          Laser printer is recommended. Ink printers may result in smudged text.
        </Step>

        <Step>
          Tags are 6 inches by 12 inches (4 inches by 7 inches for
          motorcycles). These are non-standard sizes, so the printer must
          allow for custom page sizes.
        </Step>

        <Step>
          Operating systems may install generic print drivers. These are less
          likely to handle custom sizing than drivers provided by the printer
          manufacturer.
        </Step>

        <Step>
          The tag paper is heavier and slicker than normal printer paper and
          may cause jams in some printers.
        </Step>
      </View>

      <View>
        <Text style={{ fontWeight: 600 }}>Enabling Overlay Printing</Text>

        <Step number={1}>
          Go to the "Account" menu, then select "Profile".
        </Step>

        <Step number={2}>
          Towards the bottom of the form, click switch next to "Enable
          printing purchase information overlay?" so the that switch
          turns green. Then click "Save".
        </Step>

        <Text>
          Note that this is a per-user setting, so if there are multiple users
          at your dealership, each user can enable or disable this feature
          without affecting other users.
        </Text>
      </View>

      <Text style={{ fontWeight: 600 }}>Printer Testing and Alignment</Text>

      <Text>
        On the Profile screen, underneath the "Enable" switch is a link to
        to a screen,
        <Link to="/alignment" target="_blank">
          "Click here to adjust print alignment"
        </Link>,
        that includes a PDF you can print to test your printer setup. There
        is a also a form that lets you adjust alignment if your printer
        offsets the PDF slightly on the printed tag.
      </Text>

      <Text>
        For Car tags, you will need to cut a sheet of paper 6" x 12".
        Tabloid size paper (12" x 18") can be easily cut into 3
        sheets. You can also pick up alignment sheets that are pre-cut and
        have alignment guides pre-printed from the ADR office at 813 NW 34th
        St, Moore, OK). Motorcycle tags are 4" x 7".
      </Text>

      <View>
        <Step number={1}>
          Click the appropriate "Show ... Tag Alignment PDF" link ("Car" will be
          selected by default).
        </Step>

        <Step number={2}>
          Click the "Print Alignment PDF" button.
        </Step>

        <Step number={3}>
          You will now need to choose the appropriate printer settings,
          which may take experimenting. For example, you will likely need to
          set a custom page size of 6" x 12" (or 4" x 7" for motorcycle tags).
          You may also need to use a bypass tray, or set the paper type to
          heavyweight and/or glossy.
        </Step>

        <Step number={4}>
          On some printers, you may need to offset the print slightly. To help
          with this, the alignment sheet has several grids around the areas
          where overlay information will be printed. You can compare this to a
          tag (or on an alignment template) to see if adjustments are needed.
          Lines on the grids are 1/8 of an inch apart. If adjustments are
          needed, enter the offset amounts (negative numbers are fine) in the
          form on the Print Alignment screen, then click "Save Offsets".
        </Step>
      </View>

      <Text style={{ fontWeight: 800 }}>
        Printing Purchase Information Overlay
      </Text>

      <Text>
        Once printing purchase information is enabled for your account, when
        you enter pre-registration information for a temporary tag, there will
        be an additional button at the bottom of the form "Save and Print
        Overlay". Click this instead of the normal "Save" button to be taken
        to the "Print Overlay" screen.
      </Text>

      <Text>
        Ensure the expected information is displayed, then click "Print" in
        the bottom right. Adjust printer settings as needed and print. Once
        done, click "Close" or click a menu item to leave the Print screen.
      </Text>

      <Text>
        SB2035 allows you to print "See Bill of Sale" instead of the
        purchaser's name if the purchaser keeps the Bill of Sale in the
        vehicle. There is a toggle above the print preview to print "See Bill
        of Sale" instead of the purchase information.
      </Text>

      <Text style={{ fontWeight: 800 }}>Printing Later</Text>

      <Text>
        Prior to the pre-registration being submitted to Service Oklahoma,
        which occurs overnight, there is a link to print the overlay if you
        did not do so just after saving the pre-registration information. Go
        to the "Pre-Registration History" and click "Print Overlay" in the
        "Actions" column.
      </Text>
    </View>
  );
};

export default PrintOverlay;
